/**
 * Phase 6.3.1 — SA Edit Profile.
 *
 * Hits PUT /api/users/profile (existing endpoint) with name/phone updates.
 * Used by operators (dispatcher + super_admin) to fix the name shown across
 * the app (e.g. when an admin email update left `name` set to the email value).
 *
 * Email cannot be changed here — that requires escalated workflow. Customer
 * profile fields like `address` are intentionally hidden for operators.
 */
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/users/profile`,
        { name, phone },
        { withCredentials: true },
      );
      toast.success('Profile updated');
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] scanline-overlay dispatch-safe-top">
      <Navbar />
      <div className="max-w-xl mx-auto p-4 sm:p-8">
        <button
          onClick={() => navigate(-1)}
          className="text-[#A3C7A8] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider flex items-center gap-2 mb-4"
          data-testid="ep-back"
        >
          <ArrowLeft className="w-4 h-4" /> // BACK
        </button>

        <div className="border border-[#2A0F3D] bg-[#0C0612] p-6">
          <h1 className="text-[#39FF14] font-mono text-xl font-bold uppercase tracking-wider mb-2">
            {'>'} EDIT_PROFILE_
          </h1>
          <p className="text-[#A3C7A8] font-mono text-xs mb-6">
            {`// Fixes the name + phone shown across the app. Email can't be changed here.`}
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Email (read-only)</Label>
              <Input
                value={user?.email || ''}
                readOnly
                className="bg-[#050003]/60 border-[#2A0F3D] text-[#6B8E73] font-mono rounded-none"
                data-testid="ep-email-readonly"
              />
            </div>
            <div>
              <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                data-testid="ep-name"
                placeholder="Your name"
              />
            </div>
            <div>
              <Label className="text-[#6B8E73] font-mono text-[10px] uppercase tracking-widest">Phone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                data-testid="ep-phone"
                placeholder="(555) 123-4567"
              />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#39FF14] text-black hover:bg-[#2bcc10] font-mono text-xs uppercase tracking-wider rounded-none mt-6"
              data-testid="ep-save"
            >
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
